/**
 * Vector - 3D Vector class for geometry operations
 *
 * Immutable vector operations for CAD calculations.
 * All operations return new Vector instances.
 */

export class Vector {
  constructor(x = 0, y = 0, z = 0) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  // Static constructors
  static zero() { return new Vector(0, 0, 0); }
  static unitX() { return new Vector(1, 0, 0); }
  static unitY() { return new Vector(0, 1, 0); }
  static unitZ() { return new Vector(0, 0, 1); }

  static fromArray(arr) {
    return new Vector(arr[0] || 0, arr[1] || 0, arr[2] || 0);
  }

  static fromObject(obj) {
    return new Vector(obj.x || 0, obj.y || 0, obj.z || 0);
  }

  // Basic operations
  add(v) {
    return new Vector(this.x + v.x, this.y + v.y, this.z + v.z);
  }

  subtract(v) {
    return new Vector(this.x - v.x, this.y - v.y, this.z - v.z);
  }

  multiply(scalar) {
    return new Vector(this.x * scalar, this.y * scalar, this.z * scalar);
  }

  divide(scalar) {
    if (scalar === 0) throw new Error('Division by zero');
    return new Vector(this.x / scalar, this.y / scalar, this.z / scalar);
  }

  negate() {
    return new Vector(-this.x, -this.y, -this.z);
  }

  // Vector products
  dot(v) {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  cross(v) {
    return new Vector(
      this.y * v.z - this.z * v.y,
      this.z * v.x - this.x * v.z,
      this.x * v.y - this.y * v.x
    );
  }

  // Length operations
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }

  lengthSquared() {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  normalize() {
    const len = this.length();
    if (len === 0) return new Vector(0, 0, 0);
    return this.divide(len);
  }

  // Distance
  distanceTo(v) {
    return this.subtract(v).length();
  }

  distanceToSquared(v) {
    return this.subtract(v).lengthSquared();
  }

  // Angle operations
  angleTo(v) {
    const dot = this.dot(v);
    const len = this.length() * v.length();
    if (len === 0) return 0;
    return Math.acos(Math.max(-1, Math.min(1, dot / len)));
  }

  angleToXY() {
    return Math.atan2(this.y, this.x);
  }

  // Transformations
  rotate2D(angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Vector(
      this.x * cos - this.y * sin,
      this.x * sin + this.y * cos,
      this.z
    );
  }

  rotateAroundAxis(axis, angle) {
    const k = axis.normalize();
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Rodrigues' rotation formula
    const term1 = this.multiply(cos);
    const term2 = k.cross(this).multiply(sin);
    const term3 = k.multiply(k.dot(this) * (1 - cos));

    return term1.add(term2).add(term3);
  }

  scale(sx, sy = sx, sz = sx) {
    return new Vector(this.x * sx, this.y * sy, this.z * sz);
  }

  mirror(normal) {
    const n = normal.normalize();
    const d = 2 * this.dot(n);
    return this.subtract(n.multiply(d));
  }

  // Interpolation
  lerp(v, t) {
    return new Vector(
      this.x + (v.x - this.x) * t,
      this.y + (v.y - this.y) * t,
      this.z + (v.z - this.z) * t
    );
  }

  // Projections
  projectOnto(v) {
    const vNorm = v.normalize();
    return vNorm.multiply(this.dot(vNorm));
  }

  projectOntoPlane(normal) {
    const n = normal.normalize();
    return this.subtract(n.multiply(this.dot(n)));
  }

  // Comparison
  equals(v, tolerance = 1e-10) {
    return Math.abs(this.x - v.x) < tolerance &&
           Math.abs(this.y - v.y) < tolerance &&
           Math.abs(this.z - v.z) < tolerance;
  }

  isZero(tolerance = 1e-10) {
    return this.length() < tolerance;
  }

  isParallelTo(v, tolerance = 1e-10) {
    return this.cross(v).length() < tolerance;
  }

  isPerpendicularTo(v, tolerance = 1e-10) {
    return Math.abs(this.dot(v)) < tolerance;
  }

  // Conversion
  toArray() {
    return [this.x, this.y, this.z];
  }

  toObject() {
    return { x: this.x, y: this.y, z: this.z };
  }

  clone() {
    return new Vector(this.x, this.y, this.z);
  }

  toString() {
    return `Vector(${this.x.toFixed(3)}, ${this.y.toFixed(3)}, ${this.z.toFixed(3)})`;
  }
}

export default Vector;
